// The terms the deal flow adds to the form (TX-US-0043): the proposal's Incoterm and its named
// place, the quantity tolerance, origin, destination, delivery period, payment terms and logistics.
// A deal is created at Proposal, so every one of them is on the form from the start. `marks` says
// which the next move waits on and which an approved round locked (FieldMark.jsx).

import { FieldFlags, Locked } from './FieldMark.jsx';
import IncotermFacts from './IncotermFacts.jsx';
import ListSelect from './fields/ListSelect.jsx';
import LocationSelect from './fields/LocationSelect.jsx';
import { incotermOf, namedPlaceLabel } from './vocabulary.js';

// trex/deals/fields.py
const MAX_NAMED_PLACE_LENGTH = 200;
const MAX_LOGISTICS_LENGTH = 4000;

export default function DealTermsFields({
  draft, set, lists, vocabulary, fieldErrors, canAddEntries, marks,
}) {
  const term = incotermOf(vocabulary, draft.incoterm);
  const flags = (field) => <FieldFlags field={field} marks={marks} />;

  return (
    <fieldset>
      <legend>Terms</legend>

      <Locked field="incoterms" marks={marks}>
        <label className="field">
          <span className="field-label">Incoterm{flags('incoterms')}</span>
          <select value={draft.incoterm} onChange={(e) => set('incoterm')(e.target.value)}>
            <option value="">—</option>
            {vocabulary.incoterms.map((incoterm) => (
              <option key={incoterm.code} value={incoterm.code}>
                {incoterm.code} - {incoterm.name}
              </option>
            ))}
          </select>
          {fieldErrors.incoterms && <span className="field-error">{fieldErrors.incoterms}</span>}
          {vocabulary.error && (
            <span className="field-error">The Incoterms could not be read: {vocabulary.error}</span>
          )}
        </label>
      </Locked>
      <IncotermFacts term={term} />

      <Locked field="named_place" marks={marks}>
        <label className="field">
          <span className="field-label">
            {namedPlaceLabel(vocabulary, draft.incoterm)}{flags('named_place')}
          </span>
          <input value={draft.namedPlace} onChange={(e) => set('namedPlace')(e.target.value)}
                 maxLength={MAX_NAMED_PLACE_LENGTH} autoComplete="off" />
          {fieldErrors.named_place && (
            <span className="field-error">{fieldErrors.named_place}</span>
          )}
        </label>
      </Locked>

      <Locked field="quantity_tolerance_pct" marks={marks}>
        <label className="field">
          <span className="field-label">Quantity tolerance ±%{flags('quantity_tolerance_pct')}</span>
          <input value={draft.tolerance} onChange={(e) => set('tolerance')(e.target.value)}
                 inputMode="decimal" autoComplete="off" />
          <span className="hint">Empty means no tolerance was agreed.</span>
          {fieldErrors.quantity_tolerance_pct && (
            <span className="field-error">{fieldErrors.quantity_tolerance_pct}</span>
          )}
        </label>
      </Locked>

      <LocationSelect label="Origin" locations={lists.locations} value={draft.origin}
                      onChange={set('origin')} error={fieldErrors.origin}
                      onAdded={lists.refresh} />
      <LocationSelect label="Destination" locations={lists.locations} value={draft.destination}
                      onChange={set('destination')} error={fieldErrors.destination}
                      onAdded={lists.refresh} />

      <Locked field="delivery_period" marks={marks}>
        <div className="field date-range">
          <span className="field-label">Delivery period{flags('delivery_period')}</span>
          <span className="field-control">
            <input type="date" aria-label="Delivery period from" value={draft.periodFrom}
                   onChange={(e) => set('periodFrom')(e.target.value)} />
            <span aria-hidden="true">to</span>
            <input type="date" aria-label="Delivery period to" value={draft.periodTo}
                   min={draft.periodFrom || undefined}
                   onChange={(e) => set('periodTo')(e.target.value)} />
          </span>
          {fieldErrors.delivery_period && (
            <span className="field-error">{fieldErrors.delivery_period}</span>
          )}
        </div>
      </Locked>

      <Locked field="payment_terms" marks={marks}>
        <ListSelect label="Payment terms" listName="payment_terms" entries={lists.payment_terms}
                    value={draft.paymentTerms} onChange={set('paymentTerms')}
                    error={fieldErrors.payment_terms} onAdded={lists.refresh}
                    canAdd={canAddEntries} flag={flags('payment_terms')} />
      </Locked>

      <label className="field">
        <span className="field-label">Logistics</span>
        <textarea value={draft.logistics} onChange={(e) => set('logistics')(e.target.value)}
                  rows={3} maxLength={MAX_LOGISTICS_LENGTH} />
        <span className="hint">Transport legs, storage, where the goods go.</span>
        {fieldErrors.logistics && <span className="field-error">{fieldErrors.logistics}</span>}
      </label>
    </fieldset>
  );
}
